/**
 * Data Flow Manager TypeScript Types
 */

import { Node as ReactFlowNode, Edge as ReactFlowEdge } from 'reactflow';

// Node types
export type NodeType =
  | 'mqtt_sensor'
  | 'http_api'
  | 'database'
  | 'file_upload'
  | 'webhook'
  | 'pulsar_topic'
  | 'iot_device'
  | 'mapper'
  | 'filter'
  | 'aggregator'
  | 'contextualizer'
  | 'validator'
  | 'enricher'
  | 'rules_engine'
  | 'conditional'
  | 'switch'
  | 'loop'
  | 'ai_decision'
  | 'agent_executor'
  | 'webhook_call'
  | 'database_write'
  | 'notification'
  | 'changeset'
  | 'pulsar_publish'
  | 'timescaledb'
  | 'mongodb'
  | 'api_endpoint'
  | 'dashboard'
  | 's3_bucket';

// Node categories
export const NODE_CATEGORIES = {
  sources: ['mqtt_sensor', 'http_api', 'database', 'file_upload', 'webhook', 'pulsar_topic', 'iot_device'],
  transforms: ['mapper', 'filter', 'aggregator', 'contextualizer', 'validator', 'enricher'],
  logic: ['rules_engine', 'conditional', 'switch', 'loop', 'ai_decision'],
  actions: ['agent_executor', 'webhook_call', 'database_write', 'notification', 'changeset', 'pulsar_publish'],
  destinations: ['timescaledb', 'mongodb', 'api_endpoint', 'dashboard', 's3_bucket']
};

// Node labels
export const NODE_LABELS: Record<NodeType, string> = {
  mqtt_sensor: 'MQTT Sensor',
  http_api: 'HTTP API',
  database: 'Database Query',
  file_upload: 'File Upload',
  webhook: 'Webhook Listener',
  pulsar_topic: 'Pulsar Topic',
  iot_device: 'IoT Device',
  mapper: 'Data Mapper',
  filter: 'Filter',
  aggregator: 'Aggregator',
  contextualizer: 'IKU Contextualizer',
  validator: 'Validator',
  enricher: 'Data Enricher',
  rules_engine: 'Rules Engine',
  conditional: 'Conditional Branch',
  switch: 'Switch Case',
  loop: 'Loop Iterator',
  ai_decision: 'AI Decision Point',
  agent_executor: 'Agent Action',
  webhook_call: 'Webhook Call',
  database_write: 'Database Write',
  notification: 'Send Notification',
  changeset: 'Create Changeset',
  pulsar_publish: 'Pulsar Publish',
  timescaledb: 'TimescaleDB',
  mongodb: 'MongoDB',
  api_endpoint: 'API Endpoint',
  dashboard: 'Dashboard Widget',
  s3_bucket: 'S3/Spaces Storage'
};

// Flow node data
export interface FlowNodeData {
  label?: string;
  description?: string;
  config?: Record<string, any>;
}

// Flow node
export interface FlowNode extends ReactFlowNode<FlowNodeData> {
  type: NodeType;
}

// Flow edge
export interface FlowEdge extends ReactFlowEdge {}

// Flow definition
export interface Flow {
  id: string;
  tenant?: string;
  name: string;
  description?: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  status: 'draft' | 'active' | 'disabled';
  nodeCount?: number;
  edgeCount?: number;
  lastExecutedAt?: string;
  executionCount?: number;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  metadata?: Record<string, any>;
}

// Flow execution
export interface FlowExecution {
  id: string;
  flowId: string;
  tenant: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  startedBy: string;
  completedAt?: string;
  inputData?: Record<string, any>;
  nodes?: ExecutionNode[];
  executedNodeCount?: number;
  error?: string;
}

// Execution node result
export interface ExecutionNode {
  nodeId: string;
  type: NodeType;
  status: 'success' | 'error';
  duration?: number;
  outputData?: any;
  error?: string;
}

export type FlowEventStatus = 'started' | 'running' | 'success' | 'error' | 'queued' | 'completed' | 'failed';
export type FlowEventKind = 'node' | 'edge' | 'execution' | 'pipeline' | 'telemetry';
export type FlowEventDirection = 'forward' | 'reverse' | 'bidirectional';

export interface FlowRunEvent {
  source: 'dataflow' | 'api_ingest' | 'telemetry';
  kind: FlowEventKind;
  status: FlowEventStatus;
  ts: string;
  execution_id?: string;
  flow_id?: string;
  pipeline_id?: string;
  run_id?: string;
  node_id?: string;
  node_type?: string;
  source_id?: string;
  target_id?: string;
  direction?: FlowEventDirection;
  message?: string;
  error?: string;
  detail?: Record<string, any>;
}

// Template
export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  nodeCount: number;
  edgeCount: number;
  nodes?: FlowNode[];
  edges?: FlowEdge[];
}

// Rule definition
export interface RuleDefinition {
  id: string;
  name: string;
  condition: RuleCondition;
  actions: RuleAction[];
}

// Rule condition
export interface RuleCondition {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'outside';
  value: any;
  unit?: string;
}

// Rule action
export interface RuleAction {
  type: 'alert' | 'webhook' | 'agent_executor' | 'notification' | 'changeset' | 'ai-queue';
  severity?: 'info' | 'warning' | 'error' | 'critical';
  channel?: string;
  recipient?: string;
  message?: string;
  config?: Record<string, any>;
}

// Agent configuration
export interface AgentConfig {
  agentId?: string;
  agentName: string;
  domain: string;
  template: string;
  customize?: Record<string, any>;
}

// Flow metrics
export interface FlowMetrics {
  executions: number;
  completed: number;
  failed: number;
  successRate: string;
  nodes: NodeMetrics[];
}

// Node metrics
export interface NodeMetrics {
  nodeId: string;
  type: NodeType;
  executions: number;
  successes: number;
  failures: number;
  avgDuration: number;
  successRate: string;
}

// API ingest pipeline types (system flows)
export interface ApiEndpoint {
  endpoint_id: string;
  method: string;
  path: string;
  description?: string;
  entity_type?: string;
  template_id?: string;
  tags?: string[];
}

export interface ExternalApi {
  api_id: string;
  api_name: string;
  provider: string;
  base_url?: string;
  version?: string;
  status?: string;
  endpoints?: ApiEndpoint[];
}

export interface ApiIngestPipeline {
  pipeline_id: string;
  tenant?: string;
  app_id?: string | null;
  api_id: string;
  endpoint_id: string;
  schedule?: {
    every_seconds?: number;
  };
  request?: Record<string, any>;
  ingest_mode: 'pulsar' | 'timescale' | 'dual';
  store_mode: 'telemetry' | 'api_telemetry' | 'both' | 'pim';
  enabled: boolean;
  status?: string;
  last_run_at?: string;
  last_success_at?: string;
  next_run_at?: string;
}

export type SystemFlowCategory =
  | 'api_ingest'
  | 'sensor'
  | 'sensor_type'
  | 'iot_shack'
  | 'pim'
  | 'dam'
  | 'public_api'
  | 'pipeline';

export type FlowType =
  | 'device_telemetry'
  | 'device_model_telemetry'
  | 'api_ingest'
  | 'public_api'
  | 'pipeline';

export interface FlowEntity {
  type: string;
  id: string;
  label?: string;
}

export interface FlowSource {
  system: string;
  details?: Record<string, any>;
}

export interface FlowPipeline {
  mode?: string | null;
  store?: string | null;
  schedule?: Record<string, any> | null;
}

export interface FlowSchema {
  standard: string;
  model: string;
  version: string;
}

export interface SystemFlow extends Flow {
  contract_version?: string;
  flow_type?: FlowType;
  entity?: FlowEntity | null;
  source?: FlowSource | null;
  pipeline?: FlowPipeline | null;
  schema?: FlowSchema | null;
  metadata?: {
    category?: SystemFlowCategory;
    [key: string]: any;
  };
}

export type PipelineType =
  | 'api_ingest'
  | 'sensor'
  | 'sensor_type'
  | 'iot_shack'
  | 'pim'
  | 'dam'
  | 'public_api'
  | 'custom';

export interface DataflowPipeline {
  id: string;
  tenant?: string;
  app_id?: string | null;
  type: PipelineType;
  name: string;
  description?: string;
  spec: Record<string, any>;
  status?: string;
  external?: {
    service: string;
    pipeline_id?: string;
  } | null;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface TelemetryActivity {
  deviceId?: string;
  measurementType?: string;
  ts?: string;
  value?: number | string;
  uom?: string;
}

// ------------------------------------------------------------------ //
// Operational Status (OpsStatus) types
// ------------------------------------------------------------------ //

export type PipelineHealth = 'healthy' | 'degraded' | 'stale' | 'error';

export interface OpsStatusPipeline {
  pipeline_id: string;
  api_id: string;
  api_name: string;
  endpoint_id?: string | null;
  status: 'enabled' | 'disabled';
  interval_seconds?: number | null;
  last_run_at?: string | null;
  last_success_at?: string | null;
  consecutive_errors: number;
  success_count: number;
  records_last_run?: number | null;
  records_24h?: number | null;
  census_store?: string | null;
  last_error?: string | null;
  store_mode?: string | null;
  app_id?: string | null;
  health: PipelineHealth;
  display_status?: string | null;
}

export interface OpsStatusApiPipelines {
  total: number;
  enabled: number;
  disabled: number;
  error_count: number;
  pipelines: OpsStatusPipeline[];
}

export interface OpsStatusIotDevices {
  total: number;
  by_status: Record<string, number>;
  devices_with_recent_data: number;
  last_data_at?: string | null;
}

export interface OpsStatusCredentials {
  total_apis: number;
  vault_secured: number;
  plaintext: number;
  expiring_soon: any[];
}

export interface OpsStatusDataVolume {
  telemetry_rows?: number | null;
  api_telemetry_rows?: number | null;
  ingest_history_total?: number | null;
  ingest_last_24h?: number | null;
  census_store?: string | null;
  census_timestamp_fields?: string[];
}

export interface OpsStatus {
  success: boolean;
  cached?: boolean;
  api_pipelines: OpsStatusApiPipelines;
  iot_devices: OpsStatusIotDevices;
  credentials: OpsStatusCredentials;
  data_volume: OpsStatusDataVolume;
  generated_at: string;
}

export interface ConnectionHealth {
  type: 'api_pipeline' | 'iot_device';
  pipeline_id?: string;
  api_id?: string;
  api_name?: string;
  endpoint_id?: string | null;
  device_id?: string;
  device_type?: string | null;
  label?: string | null;
  health: PipelineHealth | 'disconnected';
  last_data_at?: string | null;
  records_last_run?: number | null;
  interval_seconds?: number | null;
  store_mode?: string | null;
}

export interface ConnectionHealthResponse {
  success: boolean;
  entity_query: { api_definition_id?: string; device_id?: string };
  connections: ConnectionHealth[];
}
